import { useState, useEffect, useRef } from "react";
import { MapPin, Building2, Play, Loader2 } from "lucide-react";
import styles from "./CameraCard.module.css";

interface CameraCardProps {
  id: string;
  documentId: string;
  name: string;
  address: string;
  object: string;
  status: "active" | "inactive";
  streamUrl?: string;
  thumbnailUrl?: string;
  onClick: (id: string) => void;
}

// Placeholder SVG в base64 (иконка камеры на тёмном фоне)
const PLACEHOLDER_SVG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='320' height='180' viewBox='0 0 320 180'%3E%3Crect fill='%231a1a1a' width='320' height='180'/%3E%3Ccircle cx='160' cy='90' r='30' fill='%23333'/%3E%3Ccircle cx='160' cy='90' r='20' fill='%23444'/%3E%3Ccircle cx='160' cy='90' r='8' fill='%23555'/%3E%3C/svg%3E";

export function CameraCard({
  documentId,
  name,
  address,
  object,
  status,
  streamUrl,
  thumbnailUrl,
  onClick,
}: CameraCardProps) {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Определяем URL для превью
  const previewSrc = imageError
    ? PLACEHOLDER_SVG
    : thumbnailUrl ||
      (streamUrl ? `${streamUrl}/preview.jpg` : PLACEHOLDER_SVG);

  // Сбрасываем состояние при смене src
  useEffect(() => {
    if (previewSrc !== PLACEHOLDER_SVG) {
      setImageLoading(true);
      setImageError(false);
    }
  }, [previewSrc]);

  // Проверяем загрузку при монтировании
  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) {
      setImageLoading(false);
    }
  }, []);

  const showSpinner = imageLoading && !imageError;

  const handleClick = () => {
    onClick(documentId);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick(documentId);
    }
  };

  return (
    <article
      className={styles.card}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Камера ${name}`}
    >
      <div className={styles.preview}>
        <img
          ref={imgRef}
          src={previewSrc}
          alt={`Превью камеры ${name}`}
          className={styles.previewImage}
          loading="lazy"
          onLoad={() => setImageLoading(false)}
          onError={() => {
            setImageLoading(false);
            setImageError(true);
          }}
        />

        {showSpinner && (
          <div className={styles.spinnerOverlay}>
            <Loader2 size={32} className={styles.spinner} />
          </div>
        )}

        <div
          className={`${styles.statusBadge} ${
            status === "active" ? styles.statusActive : styles.statusInactive
          }`}
        >
          <span className={styles.statusDot} />
          {status === "active" ? "Онлайн" : "Офлайн"}
        </div>

        <div className={styles.previewOverlay}>
          <div className={styles.playIcon}>
            <Play size={24} fill="white" />
          </div>
        </div>
      </div>

      <div className={styles.info}>
        <h3 className={styles.name} title={name}>
          {name}
        </h3>
        <div className={styles.meta}>
          <div className={styles.metaItem}>
            <MapPin size={14} className={styles.metaIcon} />
            <span className={styles.metaText} title={address}>
              {address}
            </span>
          </div>
          <div className={styles.metaItem}>
            <Building2 size={14} className={styles.metaIcon} />
            <span className={styles.metaText} title={object}>
              {object}
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}
